import { isAbortError } from 'abort-controller-x';
import { CallOptions, ClientError, ClientMiddlewareCall, Status } from 'nice-grpc-web';

import Logger from '@/utils/logger';

type MiddlewareArgs = {
  successCallback?: () => void;
  errorCallback?: (error: any) => void;
};

export function middleware({ successCallback, errorCallback }: MiddlewareArgs) {
  return async function* closedMiddleware<Request, Response>(
    call: ClientMiddlewareCall<Request, Response>,
    options: CallOptions,
  ) {
    const {
      request,
      method: { path },
    } = call;

    const isProd = process.env.IS_PRODUCTION;

    if (!isProd) {
      Logger.log([
        {
          type: 'info',
          message: 'GRPC',
          items: [
            {
              message: `Client call: ${path}`,
            },
            {
              message: `Request:`,
              items: [
                {
                  message: request,
                },
              ],
            },
          ],
        },
      ]);
    }

    try {
      const result = yield* call.next(request, options);

      if (!isProd) {
        Logger.log([
          {
            type: 'success',
            message: 'GRPC',
            items: [
              {
                message: `Client received: ${path}`,
              },
              {
                message: `Request:`,
                items: [
                  {
                    message: request,
                  },
                ],
              },
              {
                message: `Response:`,
                items: [
                  {
                    message: result,
                  },
                ],
              },
            ],
          },
        ]);
      }

      successCallback?.();

      return result;
    } catch (error: any) {
      let errorMessage: string;
      if (error instanceof ClientError) {
        errorMessage = `status "${Status[error.code]}", ${error.details}`;
      } else if (isAbortError(error)) {
        errorMessage = `request canceled`;
      } else {
        errorMessage = error?.stack ?? 'unknown error';
      }

      if (!isProd) {
        Logger.log([
          {
            type: 'error',
            message: 'GRPC',
            items: [
              {
                message: `Client received: ${path}`,
              },
              {
                message: `Request:`,
                items: [
                  {
                    message: request,
                  },
                ],
              },
              {
                message: `Error: ${errorMessage}`,
              },
            ],
          },
        ]);
      }

      errorCallback?.(error);

      throw error;
    }
  };
}
